import { classifyCatalogImageUrl } from '../domain/googleLiveReferencePolicy.js';
import {
  CATALOG_CAZA_NEUTRAL_LABELS,
  catalogCazaLabel,
  normalizeCatalogCaza,
} from '../domain/tourismRules.js';

const CATEGORY_DESCRIPTIONS_EN = {
  cafe: 'cafe',
  cultural: 'cultural stop',
  festival: 'event',
  heritage: 'heritage stop',
  hotel: 'stay',
  nature: 'nature stop',
  religious: 'religious stop',
  restaurant: 'restaurant',
  shop: 'shop',
  health: 'health service',
};

const CATEGORY_DESCRIPTIONS_AR = {
  cafe: 'مقهى',
  cultural: 'محطة ثقافية',
  festival: 'فعالية',
  heritage: 'محطة تراثية',
  hotel: 'إقامة',
  nature: 'محطة طبيعية',
  religious: 'محطة دينية',
  restaurant: 'مطعم',
  shop: 'متجر',
  health: 'خدمة صحية',
};

function text(value) {
  return String(value || '').trim();
}

function localizedName(item, lang) {
  return lang === 'ar' ? text(item?.nameAr || item?.name) : text(item?.name);
}

export function publicCazaAttribution(item, lang = 'en') {
  const isArabic = lang === 'ar';
  const cazaId = normalizeCatalogCaza(item?.caza);
  const village = isArabic
    ? text(item?.villageAr || item?.locationAr || item?.village || item?.location)
    : text(item?.village || item?.location);
  const cazaLabel = catalogCazaLabel(cazaId, lang);
  const regionLabel = isArabic ? CATALOG_CAZA_NEUTRAL_LABELS.ar : CATALOG_CAZA_NEUTRAL_LABELS.en;
  const locality = village || cazaLabel || regionLabel;
  const unnamedStop = isArabic
    ? (cazaLabel ? `محطة في ${cazaLabel}` : 'محطة في شمال لبنان')
    : (cazaLabel ? `${cazaLabel} stop` : 'North Lebanon stop');

  return {
    known: Boolean(cazaId),
    cazaId,
    cazaLabel,
    cazaLabelEn: catalogCazaLabel(cazaId, 'en'),
    locality,
    regionLabel,
    unnamedStop,
    containedInPlace: cazaId ? {
      '@type': 'AdministrativeArea',
      name: catalogCazaLabel(cazaId, 'en'),
      containedInPlace: {
        '@type': 'AdministrativeArea',
        name: 'North Governorate',
        addressCountry: 'LB',
      },
    } : null,
  };
}

function localizedPlace(item, lang) {
  return publicCazaAttribution(item, lang).locality;
}

export function publicItemDescription(item, lang = 'en') {
  const isArabic = lang === 'ar';
  const attribution = publicCazaAttribution(item, lang);
  const name = localizedName(item, lang) || attribution.unnamedStop;
  const place = attribution.locality;
  const category = isArabic
    ? (CATEGORY_DESCRIPTIONS_AR[item?.category] || 'محطة')
    : (CATEGORY_DESCRIPTIONS_EN[item?.category] || 'stop');

  if (isArabic) {
    if (attribution.known) {
      if (place === attribution.cazaLabel) {
        return `${name} هو ${category} في ${place}، شمال لبنان. تحقق من الساعات والتفاصيل محليا قبل الزيارة.`;
      }
      return `${name} هو ${category} في ${place} ضمن ${attribution.cazaLabel}، شمال لبنان. تحقق من الساعات والتفاصيل محليا قبل الزيارة.`;
    }
    return `${name} هو ${category} في ${place} ضمن شمال لبنان. تحقق من الساعات والتفاصيل محليا قبل الزيارة.`;
  }

  if (attribution.known) {
    if (place === attribution.cazaLabel) {
      return `${name} is a ${category} in ${place}, North Lebanon. Confirm hours and access locally before you go.`;
    }
    return `${name} is a ${category} in ${place}, ${attribution.cazaLabel}, North Lebanon. Confirm hours and access locally before you go.`;
  }

  return `${name} is a ${category} in ${place}, North Lebanon. Confirm hours and access locally before you go.`;
}

export function isGoogleCatalogImageUrl(value) {
  const url = text(value);
  if (!url) return false;
  const classification = classifyCatalogImageUrl(url);
  if (classification.status === 'google_place_photo' || classification.status === 'google_hosted_media') return true;
  return /^https?:\/\/(?:maps\.googleapis\.com|lh3\.googleusercontent\.com)\b/i.test(url);
}

// ADR-0004 D2: stored photos are public objects in the `place-photos` bucket, served
// as direct CDN URLs. `catalogLoader.js` owns the runtime version of this mapping, but
// it is CRA-only (extensionless imports) and cannot be imported from the node ESM SEO
// generator, so the URL shape lives here for the crawler-facing side and is tested.
const STORED_PHOTO_BUCKET = 'place-photos';

// A stored photo row is `{ path, thumb, ... }`: `path` is the 1200px master and `thumb`
// is the 400px derivative. JSON-LD deliberately takes the MASTER — a crawler wants the
// largest available and nothing downloads it on page render. This is the opposite of
// the card/thumbnail rule in the app.
function isHiddenStoredPhoto(photo) {
  return Boolean(photo && photo.hidden === true);
}

export function storedPhotoMasterUrls(photos, supabaseUrl) {
  const base = text(supabaseUrl).replace(/\/+$/, '');
  if (!base || !Array.isArray(photos)) return [];
  return photos
    .filter(photo => !isHiddenStoredPhoto(photo))
    .map(photo => text(photo && photo.path))
    .filter(Boolean)
    .map(objectPath => `${base}/storage/v1/object/public/${STORED_PHOTO_BUCKET}/${objectPath}`);
}

// ADR-0004 D4 precedence: a first-party `image_url` wins over the stored hero. A
// Google-hosted URL is never emitted from either source — it is rejected, and the
// stored first-party photo behind it is used instead when one exists.
export function publicJsonLdImage(item) {
  const photos = Array.isArray(item?.photos) ? item.photos.filter(photo => !isHiddenStoredPhoto(photo)) : [];
  const image = text(item?.image);

  if (image && !isGoogleCatalogImageUrl(image)) {
    // `catalogLoader` seeds the runtime `image` field from the stored 400px thumbnail
    // when a row has no first-party `image_url`, so an item that reached us through the
    // app carries the derivative here, not a hand-picked picture. Upgrade back to that
    // same photo's master rather than letting client-side JSON-LD downgrade the
    // prerendered one.
    const derivative = photos.find(photo => text(photo && photo.thumb) === image);
    const master = text(derivative && derivative.src);
    if (master && !isGoogleCatalogImageUrl(master)) return master;
    return image;
  }

  for (const photo of photos) {
    const src = text(photo && photo.src);
    if (src && !isGoogleCatalogImageUrl(src)) return src;
  }
  return null;
}
